import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { pick } from 'ramda';
import { AnyAction, Reducer, Store } from 'redux';

import { createSimpleReducer } from './createSimpleReducer';
import { alertReducer } from '../slices/alertSlice';
import { deleteMessageReducer } from '../slices/deleteMessageSlice';
import { replyTemplatesReducer } from '../slices/replyTemplatesSlice';
import { socketStatusReducer } from '../slices/socketStatusSlice';
import { userProfileReducer } from '../slices/userProfileSlice';
import { userReducer } from '../slices/userSlice';
import { TAppState } from '../types/appState';
import { IApplicationContext } from '../types/applicationContext';
import { TExperimentsState } from '../types/experiments';
import { TLkType } from '../types/lkType';

/** Путь до файлов, ожидающих отправки, в стейте открытого чата */
const PENDING_FILES_PATH = /^openedChats\.[^.]+\.pendingFiles/;

interface ICustomReducer extends Reducer<TAppState> {
  addReducer(reducer: Reducer<TAppState>): void;
}

function createCustomReducer(rootReducer: Reducer, rootKeys: string[]): ICustomReducer {
  const subReducers: Reducer<TAppState>[] = [];

  const reducer = (state: TAppState | undefined, action: AnyAction) => ({
    ...subReducers.reduce((acc, subReducer) => subReducer(acc, action), state),
    ...rootReducer(state && pick(rootKeys, state), action),
  });

  reducer.addReducer = (subReducer: Reducer<TAppState>) => {
    subReducers.push(subReducer);
  };

  return reducer;
}

export interface IAppStore extends Store<TAppState> {
  addReducer(reducer: Reducer<TAppState>): void;
  serialize(): TAppState;
}

interface IAppStoreConfig {
  initialState: TAppState;
  optionalParams?: {
    dispatchReducerAddedAction?: boolean;
    dispatchRunnerSagaAction?: boolean;
  };
  context: IApplicationContext;
}

export function createAppStore({ initialState, optionalParams, context }: IAppStoreConfig) {
  const rootReducers = {
    alert: alertReducer,
    deleteMessage: deleteMessageReducer,
    experiments: createSimpleReducer<TExperimentsState>('experiments', []),
    socketStatus: socketStatusReducer,
    source: createSimpleReducer<string | null>('source', null),
    user: userReducer,
    userProfile: userProfileReducer,
    lkType: createSimpleReducer<TLkType | null>('lkType', null),
    replyTemplates: replyTemplatesReducer,
  };
  const rootReducer = combineReducers(rootReducers);
  const customReducer = createCustomReducer(rootReducer, Object.keys(rootReducers));

  const store = configureStore({
    reducer: customReducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        thunk: { extraArgument: context },
        // В pendingFiles лежат объекты File: их геттер lastModifiedDate отдаёт новый Date при каждом чтении,
        // и проверка иммутабельности принимает это за мутацию стейта
        immutableCheck: {
          ignoredPaths: [PENDING_FILES_PATH],
        },
        serializableCheck: {
          ignoredActionPaths: ['payload.messagesMap', /^payload\.blobFiles/, 'payload.button.onClick'],
          ignoredPaths: [PENDING_FILES_PATH, 'alert.button.onClick'],
        },
      }),
    devTools: true,
    preloadedState: initialState,
  });

  function addReducer(reducer: Reducer<TAppState>) {
    if (optionalParams && optionalParams.dispatchReducerAddedAction) {
      store.dispatch({
        name: reducer.name,
        type: '@@store/REDUCER_ADDED',
      });
    }
    customReducer.addReducer(reducer);
  }

  function serialize(): TAppState {
    return store.getState();
  }

  return { ...store, addReducer, serialize };
}
